import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, FileUp, Image as ImageIcon, Link2, Sparkles } from "lucide-react";
import api, { API_BASE, getAccessToken, getSessionId } from "../lib/api";
import AuthImage from "../components/AuthImage";
import { useToasts } from "../components/Toasts";
import { FORGELINC_NAME, displayProductText as displayText } from "../lib/productNames";
import {
  aiStudioPackage,
  copyText,
  CUSTOMIZE_PROCESSOR_PENDING,
  DEFAULT_CUSTOMIZE_NOTES,
  DEFAULT_PLATE_NOTES,
  DEFAULT_PURGE_PROCESS_NOTES,
  DEFAULT_TRANSITION_TARGETS,
  designArtifact,
  designDeskPath,
  downloadJsonFile,
  gcodeCandidates,
  gcodeDrops,
  latestCustomize,
  latestOrcaBridge,
  latestStudioReturn,
  nextPathwayAction,
  ORCA_BRIDGE_CHECKED_MEANING,
  ORCA_BRIDGE_PURGE_NOTE,
  orcaHandoff,
  pathwayLabel,
  PIPELINE_STAGES,
  pipelineStage,
  pipelineStageLabel,
  relativeUpdated,
  workStatusLabel,
  workStatusTone,
  resolveRecordPreview,
  slicedPackageCandidates,
} from "../lib/forgelinkPathway";

function Field({ label, children }) {
  return <label className="bw-field"><small>{label}</small>{children}</label>;
}

export function ConceptForm({ businessKey, onCreated }) {
  const toast = useToasts((s) => s.push);
  const [form, setForm] = useState({ title: "", description: "", owner: "" });
  const [busy, setBusy] = useState(false);
  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const submit = async (event) => {
    event.preventDefault();
    if (!form.title.trim() || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/businesses/${businessKey}/records`, {
        area: "concept",
        title: form.title.trim(),
        status: "in_progress",
        description: form.description.trim(),
        priority: 80,
        owner: form.owner.trim() || null,
        metadata: { pathway_stage: "concept" },
      });
      toast("Product saved. Next: Open Design Desk.", "ok");
      await onCreated(data);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast(typeof detail === "string" ? detail : "Could not create the product idea.", "err");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="bw-record-form" onSubmit={submit} data-testid="forgelink-concept-form">
      <Field label="Product / idea title *">
        <input autoFocus value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Customized sports pen — lacrosse holder" data-testid="forgelink-concept-title" />
      </Field>
      <Field label="Idea / use case">
        <textarea rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Who it is for, what it must do, and the first design constraint." />
      </Field>
      <Field label="Owner">
        <input value={form.owner} onChange={(e) => set("owner", e.target.value)} placeholder="Founder / designer" />
      </Field>
      <div className="bw-form-actions">
        <button className="btn primary" disabled={!form.title.trim() || busy} data-testid="forgelink-create-concept">
          {busy ? "Saving…" : "Create product"}
        </button>
      </div>
    </form>
  );
}

async function uploadDesignFile(businessKey, recordId, file, notes) {
  const body = new FormData();
  body.append("file", file);
  if (notes) body.append("notes", notes);
  return postMultipart(`/businesses/${businessKey}/records/${recordId}/design-artifact/upload`, body);
}

async function postMultipart(path, body) {
  const headers = {};
  const token = getAccessToken();
  const sessionId = getSessionId();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (sessionId) headers["X-Session-Id"] = sessionId;
  const response = await fetch(`${API_BASE}${path}`, { method: "POST", headers, body });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(typeof payload.detail === "string" ? payload.detail : "Upload failed");
    error.response = { data: payload, status: response.status };
    throw error;
  }
  return payload;
}

function backendFileUrl(path) {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  if (path.startsWith("/api/")) return `${API_BASE}${path.slice(4)}`;
  return path.startsWith("/") ? `${API_BASE}${path}` : `${API_BASE}/${path}`;
}

async function downloadAuthedFile(path, filename) {
  const headers = {};
  const token = getAccessToken();
  const sessionId = getSessionId();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (sessionId) headers["X-Session-Id"] = sessionId;
  const response = await fetch(backendFileUrl(path), { headers });
  if (!response.ok) throw new Error("Download failed");
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename || "forgelinc-file";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function DesignAttachForm({ businessKey, record, onAttached }) {
  const toast = useToasts((s) => s.push);
  const [url, setUrl] = useState("");
  const [filename, setFilename] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    if (!file && !url.trim()) {
      toast("Upload a file or paste a design URL.", "err");
      return;
    }
    setBusy(true);
    try {
      let data;
      if (file) {
        data = await uploadDesignFile(businessKey, record.id, file, notes.trim());
      } else {
        const looksLikeImage = /\.(png|jpe?g|gif|webp|svg)(\?|$)/i.test(url.trim());
        const response = await api.post(`/businesses/${businessKey}/records/${record.id}/design-artifact`, {
          url: url.trim(),
          filename: filename.trim() || undefined,
          notes: notes.trim() || undefined,
          previewable: looksLikeImage || undefined,
        });
        data = response.data;
      }
      toast("Design saved. Next: Advance to Render.", "ok");
      await onAttached(data);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast(typeof detail === "string" ? detail : "Could not attach the design artifact.", "err");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="bw-record-form" onSubmit={submit} data-testid="forgelink-design-form">
      <Field label="Upload design file">
        <input type="file" accept=".png,.jpg,.jpeg,.gif,.webp,.svg,.stl,.obj,.glb,.gltf,.3mf,.step,.stp,.iges,.igs" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="forgelink-design-file" />
      </Field>
      <div className="bw-or">or link a file</div>
      <Field label="Design URL">
        <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…/holder.png" data-testid="forgelink-design-url" />
      </Field>
      <Field label="Filename override">
        <input value={filename} onChange={(e) => setFilename(e.target.value)} placeholder="optional, e.g. lacrosse-holder.stl" />
      </Field>
      <Field label="Notes">
        <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Revision, dimensions, or why this file is the current design." />
      </Field>
      <div className="bw-form-actions">
        <button className="btn primary" disabled={busy || (!file && !url.trim())} data-testid="forgelink-attach-design">
          {busy ? "Saving…" : "Save design and continue"}
        </button>
      </div>
    </form>
  );
}

export function PrototypeNoteForm({ businessKey, record, onSaved }) {
  const toast = useToasts((s) => s.push);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    if (!note.trim() || busy) return;
    setBusy(true);
    try {
      const previous = record.description ? `${record.description.trim()}\n\n` : "";
      const { data } = await api.patch(`/businesses/${businessKey}/records/${record.id}`, {
        area: "prototype",
        description: `${previous}Prototype: ${note.trim()}`,
        metadata: { ...(record.metadata || {}), pathway_stage: "prototype", last_prototype_note: note.trim() },
      });
      toast("Prototype note saved. Keep cooking — add another note anytime.", "ok");
      await onSaved(data);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast(typeof detail === "string" ? detail : "Could not save the prototype note.", "err");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="bw-record-form" onSubmit={submit} data-testid="forgelink-prototype-form">
      <Field label="Prototype note *">
        <textarea rows={4} autoFocus value={note} onChange={(e) => setNote(e.target.value)} placeholder="What to print or test next, fit issue, or approval." data-testid="forgelink-prototype-note" />
      </Field>
      <div className="bw-form-actions">
        <button className="btn primary" disabled={!note.trim() || busy} data-testid="forgelink-save-prototype">
          {busy ? "Saving…" : "Save note and keep cooking"}
        </button>
      </div>
    </form>
  );
}

export function AiStudioHandoffForm({ businessKey, record, onSaved }) {
  const toast = useToasts((s) => s.push);
  const existing = aiStudioPackage(record);
  const [colorNotes, setColorNotes] = useState(existing?.color_notes || "");
  const [plateNotes, setPlateNotes] = useState(existing?.plate_notes || DEFAULT_PLATE_NOTES);
  const [purgeNotes, setPurgeNotes] = useState(existing?.purge_process_notes || DEFAULT_PURGE_PROCESS_NOTES);
  const [probe, setProbe] = useState(existing?.ai_studio_probe || null);
  const [sendLive, setSendLive] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!record?.id) return undefined;
    let alive = true;
    api.get(`/businesses/${businessKey}/records/${record.id}/print-loop/ai-studio`)
      .then(({ data }) => {
        if (alive) setProbe(data.ai_studio_probe || null);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [businessKey, record?.id]);

  const live = probe?.mode === "live";

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/businesses/${businessKey}/records/${record.id}/print-loop/ai-studio`, {
        color_notes: colorNotes.trim() || undefined,
        plate_notes: plateNotes.trim() || undefined,
        purge_process_notes: purgeNotes.trim() || undefined,
        send_live: Boolean(sendLive && live),
      });
      const pack = data.package || data;
      setProbe(data.ai_studio_probe || pack.ai_studio_probe || probe);
      downloadJsonFile(`${(record.title || "forgelinc").replace(/\s+/g, "-").toLowerCase()}-ai-studio.json`, pack);
      const copied = await copyText(pack.studio_brief || "");
      const liveNote = pack.live?.reply ? " Gemini reply stored on the record." : "";
      toast((copied ? "Google AI Studio package saved, downloaded, and copied." : "Google AI Studio package saved and downloaded.") + liveNote, "ok");
      await onSaved?.(data.record || data);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const message = typeof detail === "string" ? detail : detail?.detail;
      toast(message || "Could not export the AI Studio package.", "err");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="bw-record-form" onSubmit={submit} data-testid="forgelink-ai-studio-form">
      <p className="hint">
        Destination is <b>Google AI Studio</b> (<a href="https://aistudio.google.com" target="_blank" rel="noreferrer">aistudio.google.com</a>), not a ChatGPT project.
        Design Desk pixels stay gpt-image-2.5-sunburst @ max. Return notes/files here — do not stall.
      </p>
      <p className="hint" data-testid="forgelink-ai-studio-probe">
        {live
          ? `Live Gemini key found (${probe.key_name} → ${probe.model}). Optional API send is available.`
          : "No GEMINI_API_KEY / GOOGLE_AI_* / AISTUDIO_* key in backend/.env. Package export still works. Live Gemini send is not faked."}
      </p>
      <Field label="Color / AMS notes">
        <textarea rows={3} value={colorNotes} onChange={(e) => setColorNotes(e.target.value)} placeholder="Slot 1 gray PETG HF, slot 2 white, slot 3 orange, slot 4 blue" data-testid="forgelink-ai-studio-color" />
      </Field>
      <Field label="Plate notes">
        <textarea rows={2} value={plateNotes} onChange={(e) => setPlateNotes(e.target.value)} />
      </Field>
      <Field label="Purge-process notes">
        <textarea rows={4} value={purgeNotes} onChange={(e) => setPurgeNotes(e.target.value)} data-testid="forgelink-ai-studio-purge" />
      </Field>
      {live ? (
        <label className="bw-field" style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input type="checkbox" checked={sendLive} onChange={(e) => setSendLive(e.target.checked)} data-testid="forgelink-ai-studio-send-live" />
          <small>Also send via Gemini API ({probe.model})</small>
        </label>
      ) : null}
      <div className="bw-form-actions">
        <a className="btn" href="https://aistudio.google.com" target="_blank" rel="noreferrer">Open aistudio.google.com</a>
        <button className="btn primary" disabled={busy} data-testid="forgelink-ai-studio-export">
          {busy ? "Exporting…" : "Handoff to AI Studio"}
        </button>
      </div>
    </form>
  );
}

export function StudioReturnForm({ businessKey, record, onSaved }) {
  const toast = useToasts((s) => s.push);
  const [notes, setNotes] = useState("");
  const [nextCook, setNextCook] = useState("orca");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    if (!notes.trim() && !file) {
      toast("Add Studio notes or a returned file.", "err");
      return;
    }
    setBusy(true);
    try {
      let data;
      if (file) {
        const body = new FormData();
        body.append("file", file);
        if (notes.trim()) body.append("notes", notes.trim());
        body.append("next_cook", nextCook);
        data = await postMultipart(`/businesses/${businessKey}/records/${record.id}/print-loop/studio-return/upload`, body);
      } else {
        const response = await api.post(`/businesses/${businessKey}/records/${record.id}/print-loop/studio-return`, {
          notes: notes.trim(),
          next_cook: nextCook,
        });
        data = response.data;
      }
      toast("Studio return saved on this record. Keep cooking.", "ok");
      await onSaved?.(data.record || data);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast(typeof detail === "string" ? detail : "Could not import Studio results.", "err");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="bw-record-form" onSubmit={submit} data-testid="forgelink-studio-return-form">
      <p className="hint">Drop Google AI Studio / Gemini notes and files back onto the same product. 3MF/STL here also seeds the Orca handoff.</p>
      <Field label="Studio notes *">
        <textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What Studio produced, color decisions, next cook." data-testid="forgelink-studio-return-notes" />
      </Field>
      <Field label="Returned file">
        <input type="file" accept=".png,.jpg,.jpeg,.webp,.stl,.obj,.glb,.gltf,.3mf,.gcode,.gco,.bgcode,.zip" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="forgelink-studio-return-file" />
      </Field>
      <Field label="Next cook">
        <select value={nextCook} onChange={(e) => setNextCook(e.target.value)} data-testid="forgelink-studio-return-next">
          <option value="orca">Send to Orca</option>
          <option value="orca_bridge">Orca Bridge check</option>
          <option value="customize">AI Customize G-code</option>
          <option value="design">Bounce to Design Desk</option>
          <option value="ai_studio">Handoff to AI Studio again</option>
          <option value="gcode">Attach SD-ready G-code</option>
        </select>
      </Field>
      <div className="bw-form-actions">
        <button className="btn primary" disabled={busy || (!notes.trim() && !file)} data-testid="forgelink-studio-return-save">
          {busy ? "Saving…" : "Return from Studio"}
        </button>
      </div>
    </form>
  );
}

export function OrcaHandoffForm({ businessKey, record, onSaved }) {
  const toast = useToasts((s) => s.push);
  const existing = orcaHandoff(record);
  const pack = aiStudioPackage(record);
  const [colorNotes, setColorNotes] = useState(existing?.color_notes || pack?.color_notes || "");
  const [plateNotes, setPlateNotes] = useState(existing?.plate_notes || pack?.plate_notes || DEFAULT_PLATE_NOTES);
  const [notes, setNotes] = useState(existing?.notes || "");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const files = existing?.files || [];

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    if (!file && !files.length) {
      toast("Upload a 3MF or STL for OrcaSlicer.", "err");
      return;
    }
    setBusy(true);
    try {
      let data;
      if (file) {
        const body = new FormData();
        body.append("file", file);
        if (notes.trim()) body.append("notes", notes.trim());
        if (colorNotes.trim()) body.append("color_notes", colorNotes.trim());
        if (plateNotes.trim()) body.append("plate_notes", plateNotes.trim());
        data = await postMultipart(`/businesses/${businessKey}/records/${record.id}/print-loop/orca/upload`, body);
      } else {
        const response = await api.post(`/businesses/${businessKey}/records/${record.id}/print-loop/orca`, {
          color_notes: colorNotes.trim() || undefined,
          plate_notes: plateNotes.trim() || undefined,
          notes: notes.trim() || undefined,
        });
        data = response.data;
      }
      if (data.package) downloadJsonFile(`${(record.title || "forgelinc").replace(/\s+/g, "-").toLowerCase()}-orca.json`, data.package);
      toast("Orca handoff saved. Slice in OrcaSlicer, export .gcode.3mf, then run Orca Bridge check.", "ok");
      await onSaved?.(data.record || data);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast(typeof detail === "string" ? detail : "Could not send to Orca.", "err");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="bw-record-form" onSubmit={submit} data-testid="forgelink-orca-form">
      <p className="hint">Print-ready 3MF/STL + color notes for OrcaSlicer. A mesh started on Design Desk already carries the founder low-waste purge recipe. After slicing, export the plate as .gcode.3mf and run Orca Bridge check. That check does not reduce purge. A manual SD-ready upload stays available.</p>
      {files.length ? (
        <ul className="bw-loop-files">
          {files.map((row) => (
            <li key={row.id}>
              <button type="button" className="btn small" onClick={() => downloadAuthedFile(`/businesses/${businessKey}/records/${record.id}/print-loop/files/${row.id}`, row.filename)}>
                {row.filename}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <Field label="3MF / STL">
        <input type="file" accept=".stl,.obj,.glb,.gltf,.3mf,.step,.stp" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="forgelink-orca-file" />
      </Field>
      <Field label="Color notes">
        <textarea rows={3} value={colorNotes} onChange={(e) => setColorNotes(e.target.value)} data-testid="forgelink-orca-color" />
      </Field>
      <Field label="Plate notes">
        <textarea rows={2} value={plateNotes} onChange={(e) => setPlateNotes(e.target.value)} />
      </Field>
      <Field label="Orca notes">
        <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Orientation, supports, process profile." />
      </Field>
      <div className="bw-form-actions">
        <button className="btn primary" disabled={busy || (!file && !files.length)} data-testid="forgelink-orca-save">
          {busy ? "Saving…" : "Send to Orca"}
        </button>
      </div>
    </form>
  );
}

export function OrcaBridgeForm({ businessKey, record, onSaved }) {
  const toast = useToasts((s) => s.push);
  const saved = latestOrcaBridge(record);
  const candidates = slicedPackageCandidates(record);
  const [file, setFile] = useState(null);
  const [artifactId, setArtifactId] = useState(candidates[0]?.id || "");
  const [result, setResult] = useState(saved);
  const [busy, setBusy] = useState(false);
  const shown = result || saved;

  const run = async (event) => {
    event.preventDefault();
    if (busy) return;
    if (!file && !artifactId) {
      toast("Upload a sliced .gcode.3mf or pick one already stored on this record.", "err");
      return;
    }
    setBusy(true);
    try {
      let data;
      if (file) {
        const body = new FormData();
        body.append("file", file);
        data = await postMultipart(`/businesses/${businessKey}/records/${record.id}/print-loop/orca-bridge/upload`, body);
      } else {
        const response = await api.post(`/businesses/${businessKey}/records/${record.id}/print-loop/orca-bridge`, {
          artifact_id: artifactId,
        });
        data = response.data;
      }
      setResult(data.orca_bridge || null);
      const purge = data.orca_bridge?.purge_reduction_applied;
      toast(
        data.orca_bridge?.status === "CHECKED" && purge === false
          ? "CHECKED. Package checks passed. Purge reduction was not applied."
          : "Orca Bridge finished without a CHECKED report.",
        data.orca_bridge?.status === "CHECKED" && purge === false ? "ok" : "err",
      );
      await onSaved?.(data.record || data, { keepOpen: true });
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast(typeof detail === "string" ? detail : "Orca Bridge could not check this package.", "err");
    } finally {
      setBusy(false);
    }
  };

  const plates = shown?.report?.plates && typeof shown.report.plates === "object" ? Object.entries(shown.report.plates) : [];

  return (
    <form className="bw-record-form" onSubmit={run} data-testid="forgelink-orca-bridge-form">
      <p className="bw-customize-banner" data-testid="forgelink-orca-bridge-banner">
        {ORCA_BRIDGE_CHECKED_MEANING} {ORCA_BRIDGE_PURGE_NOTE}
      </p>
      <p className="hint">Export plate sliced file / Gcode.3MF from Orca. Do not upload Save Project or plain G-code. This check never starts a printer.</p>
      <Field label="Sliced .gcode.3mf">
        <input type="file" accept=".3mf,.gcode.3mf" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="forgelink-orca-bridge-file" />
      </Field>
      {candidates.length ? (
        <Field label="Or a stored 3MF on this record">
          <select value={artifactId} onChange={(e) => setArtifactId(e.target.value)} data-testid="forgelink-orca-bridge-artifact">
            <option value="">Select a stored package</option>
            {candidates.map((row) => (
              <option value={row.id} key={row.id}>{row.label}: {row.filename}</option>
            ))}
          </select>
        </Field>
      ) : null}
      {shown ? (
        <div data-testid="forgelink-orca-bridge-result">
          <p><b>{shown.status || "CHECKED"}</b> — {shown.checked_meaning || ORCA_BRIDGE_CHECKED_MEANING}</p>
          <p data-testid="forgelink-orca-bridge-purge">purge_reduction_applied: {String(shown.purge_reduction_applied)}</p>
          {plates.length ? (
            <ul className="bw-loop-files">
              {plates.map(([name, plate]) => (
                <li key={name}>
                  {name}: tools {Array.isArray(plate?.tool_requests) ? plate.tool_requests.join(", ") : "none"} · AMS blocks {plate?.ams_blocks ?? 0} · toolpath_changes {plate?.toolpath_changes ?? 0}
                </li>
              ))}
            </ul>
          ) : null}
          <div className="bw-form-actions" style={{ justifyContent: "flex-start" }}>
            {shown.output_artifact?.id ? (
              <button
                type="button"
                className="btn small"
                onClick={() => downloadAuthedFile(`/businesses/${businessKey}/records/${record.id}/print-loop/files/${shown.output_artifact.id}`, shown.output_artifact.filename)}
                data-testid="forgelink-orca-bridge-download"
              >
                Download checked package
              </button>
            ) : null}
            {shown.report_artifact?.id ? (
              <button
                type="button"
                className="btn small"
                onClick={() => downloadAuthedFile(`/businesses/${businessKey}/records/${record.id}/print-loop/files/${shown.report_artifact.id}`, shown.report_artifact.filename)}
              >
                Download report
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
      <div className="bw-form-actions">
        <button className="btn primary" disabled={busy || (!file && !artifactId)} data-testid="forgelink-orca-bridge-run">
          {busy ? "Checking…" : "Run Orca Bridge check"}
        </button>
      </div>
    </form>
  );
}

export function CustomizeGcodeForm({ businessKey, record, onSaved }) {
  const toast = useToasts((s) => s.push);
  const saved = latestCustomize(record);
  const initialRecipe = saved?.recipe || record?.metadata?.print_loop?.customize_recipe;
  const [recipe, setRecipe] = useState(() => ({
    remove_sweeps: initialRecipe?.remove_sweeps !== false,
    remove_purges: initialRecipe?.remove_purges !== false,
    transition_targets: DEFAULT_TRANSITION_TARGETS.filter((name) => (initialRecipe?.transition_targets || DEFAULT_TRANSITION_TARGETS).includes(name)),
    notes: initialRecipe?.notes || DEFAULT_CUSTOMIZE_NOTES,
    script_path: initialRecipe?.script_path || "",
    color_notes: saved?.color_notes || orcaHandoff(record)?.color_notes || aiStudioPackage(record)?.color_notes || "",
  }));
  const [file, setFile] = useState(null);
  const [artifactId, setArtifactId] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(saved);
  const [processor, setProcessor] = useState(null);
  const candidates = gcodeCandidates(record);
  const shown = result || saved;
  const pending = (shown?.processor || processor?.id || CUSTOMIZE_PROCESSOR_PENDING) === CUSTOMIZE_PROCESSOR_PENDING;

  useEffect(() => {
    if (!record?.id) return undefined;
    let alive = true;
    api.get(`/businesses/${businessKey}/records/${record.id}/print-loop/customize`)
      .then(({ data }) => {
        if (!alive) return;
        setProcessor(data.processor || null);
        if (data.recipe?.saved) {
          setRecipe((current) => ({
            ...current,
            remove_sweeps: data.recipe.remove_sweeps !== false,
            remove_purges: data.recipe.remove_purges !== false,
            transition_targets: DEFAULT_TRANSITION_TARGETS.filter((name) => (data.recipe.transition_targets || DEFAULT_TRANSITION_TARGETS).includes(name)),
            notes: data.recipe.notes || current.notes,
            script_path: data.recipe.script_path || "",
          }));
        }
        if (data.latest) setResult((current) => current || data.latest);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [businessKey, record?.id]);

  const recipePayload = () => ({
    remove_sweeps: Boolean(recipe.remove_sweeps),
    remove_purges: Boolean(recipe.remove_purges),
    transition_targets: recipe.transition_targets,
    notes: recipe.notes.trim() || DEFAULT_CUSTOMIZE_NOTES,
    script_path: recipe.script_path.trim() || undefined,
  });

  const toggleTarget = (name) => {
    setRecipe((current) => {
      const has = current.transition_targets.includes(name);
      const next = has
        ? current.transition_targets.filter((row) => row !== name)
        : [...current.transition_targets, name];
      return { ...current, transition_targets: DEFAULT_TRANSITION_TARGETS.filter((row) => next.includes(row)) };
    });
  };

  const saveRecipe = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/businesses/${businessKey}/records/${record.id}/print-loop/customize/recipe`, recipePayload());
      toast("Customize recipe saved. G-code was not rewritten.", "ok");
      await onSaved?.(data.record || data, { keepOpen: true });
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast(typeof detail === "string" ? detail : "Could not save the customize recipe.", "err");
    } finally {
      setBusy(false);
    }
  };

  const run = async (event) => {
    event.preventDefault();
    if (busy) return;
    if (!file && !artifactId) {
      toast("Upload raw Orca G-code or choose a stored G-code artifact.", "err");
      return;
    }
    setBusy(true);
    try {
      let data;
      if (file) {
        const body = new FormData();
        body.append("file", file);
        body.append("recipe_json", JSON.stringify(recipePayload()));
        if (recipe.color_notes.trim()) body.append("color_notes", recipe.color_notes.trim());
        data = await postMultipart(`/businesses/${businessKey}/records/${record.id}/print-loop/customize/upload`, body);
      } else {
        const response = await api.post(`/businesses/${businessKey}/records/${record.id}/print-loop/customize`, {
          artifact_id: artifactId,
          recipe: recipePayload(),
          color_notes: recipe.color_notes.trim() || undefined,
        });
        data = response.data;
      }
      setResult(data.customize || null);
      setProcessor(data.processor || null);
      const processorId = data.customize?.processor || data.processor?.id;
      toast(
        processorId === CUSTOMIZE_PROCESSOR_PENDING
          ? "Recipe recorded. G-code copied unchanged — toolpaths were not rewritten."
          : "Founder low-waste recipe is on the Orca handoff. This G-code copy did not rewrite toolpaths.",
        "ok",
      );
      await onSaved?.(data.record || data, { keepOpen: true });
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast(typeof detail === "string" ? detail : "Could not customize G-code.", "err");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="bw-record-form" onSubmit={run} data-testid="forgelink-customize-form">
      <p className="bw-customize-banner" data-testid="forgelink-customize-banner">
        {pending
          ? "Processor pending_founder_recipe. Run customize records this recipe and copies the Orca file. It does not rewrite toolpaths."
          : "Founder low-waste purge recipe (founder_low_waste) is the Orca handoff default: zero off-diagonal flush, purge into support and infill, narrow prime tower. This G-code step copies the sliced file and does not rewrite toolpaths. Orca Bridge check is a package checker and is not this recipe."}
      </p>
      <Field label="Raw Orca G-code">
        <input type="file" accept=".gcode,.gco,.bgcode" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="forgelink-customize-file" />
      </Field>
      {candidates.length ? (
        <Field label="Or use a stored G-code artifact">
          <select value={artifactId} onChange={(e) => setArtifactId(e.target.value)} data-testid="forgelink-customize-artifact">
            <option value="">Upload instead</option>
            {candidates.map((row) => (
              <option key={row.id} value={row.id}>{row.label}: {row.filename}</option>
            ))}
          </select>
        </Field>
      ) : null}
      <div className="bw-form-actions" style={{ justifyContent: "flex-start", gap: 14 }}>
        <label className="bw-field" style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input type="checkbox" checked={recipe.remove_sweeps} onChange={(e) => setRecipe((current) => ({ ...current, remove_sweeps: e.target.checked }))} data-testid="forgelink-customize-sweeps" />
          <small>Remove sweeps</small>
        </label>
        <label className="bw-field" style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input type="checkbox" checked={recipe.remove_purges} onChange={(e) => setRecipe((current) => ({ ...current, remove_purges: e.target.checked }))} data-testid="forgelink-customize-purges" />
          <small>Remove purges</small>
        </label>
      </div>
      <Field label="Put color transitions into">
        <div className="bw-form-actions" style={{ justifyContent: "flex-start", gap: 14 }}>
          {DEFAULT_TRANSITION_TARGETS.map((name) => (
            <label key={name} className="bw-field" style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="checkbox"
                checked={recipe.transition_targets.includes(name)}
                onChange={() => toggleTarget(name)}
                data-testid={`forgelink-customize-target-${name}`}
              />
              <small>{name === "asymmetric_tower" ? "small asymmetric tower" : name === "infill" ? "inner fill (infill)" : "supports"}</small>
            </label>
          ))}
        </div>
      </Field>
      <Field label="Recipe notes">
        <textarea rows={4} value={recipe.notes} onChange={(e) => setRecipe((current) => ({ ...current, notes: e.target.value }))} data-testid="forgelink-customize-notes" />
      </Field>
      <Field label="Color notes (optional)">
        <textarea rows={2} value={recipe.color_notes} onChange={(e) => setRecipe((current) => ({ ...current, color_notes: e.target.value }))} placeholder="AMS slots or colorway. 3MF stays on the Orca handoff." />
      </Field>
      <Field label="Script path (stored only)">
        <input value={recipe.script_path} onChange={(e) => setRecipe((current) => ({ ...current, script_path: e.target.value }))} placeholder="Not executed. See the plug doc to replace customize_gcode." data-testid="forgelink-customize-script" />
      </Field>
      {shown?.report ? (
        <div data-testid="forgelink-customize-report">
          <p className="hint">{shown.report.summary}</p>
          <p className="hint">Requested sweeps {String(shown.report.requested?.remove_sweeps)} · applied {shown.report.applied?.remove_sweeps}. Requested purges {String(shown.report.requested?.remove_purges)} · applied {shown.report.applied?.remove_purges}. Processor {shown.processor}. SD-ready {shown.sd_ready ? "yes" : "no"}.</p>
          {shown.report.preview_text ? <pre className="bw-gcode-preview" data-testid="forgelink-customize-preview">{shown.report.preview_text}</pre> : <p className="hint">No text preview (binary G-code or empty head).</p>}
          <div className="bw-form-actions" style={{ justifyContent: "flex-start" }}>
            {shown.output_artifact?.id ? (
              <button
                type="button"
                className="btn small"
                onClick={() => downloadAuthedFile(`/businesses/${businessKey}/records/${record.id}/print-loop/files/${shown.output_artifact.id}`, shown.output_artifact.filename)}
                data-testid="forgelink-customize-download"
              >
                Download customize output
              </button>
            ) : null}
            <button
              type="button"
              className="btn small"
              onClick={async () => {
                const ok = await copyText(shown.sd_path_note || shown.output_artifact?.storage_key || "");
                toast(ok ? "SD path note copied." : "Could not copy the path note.", ok ? "ok" : "err");
              }}
              data-testid="forgelink-customize-copy-path"
            >
              Copy SD path note
            </button>
          </div>
        </div>
      ) : null}
      <div className="bw-form-actions">
        <button type="button" className="btn" disabled={busy} onClick={saveRecipe} data-testid="forgelink-customize-save-recipe">
          {busy ? "Saving…" : "Save recipe"}
        </button>
        <button className="btn primary" disabled={busy || (!file && !artifactId)} data-testid="forgelink-customize-run">
          {busy ? "Running…" : "Run customize"}
        </button>
      </div>
    </form>
  );
}

export function GcodeDropForm({ businessKey, record, onSaved }) {
  const toast = useToasts((s) => s.push);
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const drops = gcodeDrops(record);

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    if (!file) {
      toast("Choose the G-code file to attach.", "err");
      return;
    }
    setBusy(true);
    try {
      const body = new FormData();
      body.append("file", file);
      if (notes.trim()) body.append("notes", notes.trim());
      const data = await postMultipart(`/businesses/${businessKey}/records/${record.id}/print-loop/gcode/upload`, body);
      toast("Manual SD-ready G-code attached. Printer-farm upload stays parked.", "ok");
      await onSaved?.(data.record || data);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast(typeof detail === "string" ? detail : "Could not attach G-code.", "err");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="bw-record-form" onSubmit={submit} data-testid="forgelink-gcode-form">
      <p className="hint">Manual attach if you already have a final G-code, or if you want to replace the customize output. Copy to SD first — live upload-to-printer is not in this slice. Customize does not claim sweeps were removed. A CHECKED Orca Bridge package is a separate file: package checks passed, not purge reduction.</p>
      {drops.length ? (
        <ul className="bw-loop-files">
          {drops.map((row) => (
            <li key={row.id}>
              <button
                type="button"
                className="btn small"
                onClick={() => downloadAuthedFile(`/businesses/${businessKey}/records/${record.id}/print-loop/files/${row.artifact?.id}`, row.artifact?.filename)}
              >
                {row.artifact?.filename || "gcode"} · SD-ready
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <Field label="SD-ready G-code *">
        <input type="file" accept=".gcode,.gco,.bgcode" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="forgelink-gcode-file" />
      </Field>
      <Field label="Notes">
        <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Post-process version, SD card label, printer." />
      </Field>
      <div className="bw-form-actions">
        <button className="btn primary" disabled={busy || !file} data-testid="forgelink-gcode-save">
          {busy ? "Saving…" : "Attach SD-ready G-code"}
        </button>
      </div>
    </form>
  );
}

export function NextCookForm({ businessKey, record, onDesk, onLoop, onSaved }) {
  const toast = useToasts((s) => s.push);
  const [busy, setBusy] = useState(false);

  const run = async (target) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/businesses/${businessKey}/records/${record.id}/print-loop/next-cook`, { target });
      toast("Next cook marked. The product stays in motion.", "ok");
      await onSaved?.(data.record || data);
      if (target === "design") onDesk?.(data.record || record);
      else onLoop?.(target, data.record || record);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast(typeof detail === "string" ? detail : "Could not mark next cook.", "err");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bw-record-form" data-testid="forgelink-next-cook-form">
      <p className="hint">An SD-ready file is on this record. Pick the next cook — Design Desk, AI Studio, and Customize stay live. No dead stage.</p>
      <div className="bw-form-actions" style={{ justifyContent: "flex-start", flexWrap: "wrap" }}>
        <button type="button" className="btn primary" disabled={busy} onClick={() => run("design")} data-testid="forgelink-next-cook-design">Open Design Desk</button>
        <button type="button" className="btn" disabled={busy} onClick={() => run("ai_studio")}>Handoff to AI Studio again</button>
        <button type="button" className="btn" disabled={busy} onClick={() => run("orca")}>Revise Orca handoff</button>
        <button type="button" className="btn" disabled={busy} onClick={() => run("orca_bridge")}>Orca Bridge check again</button>
        <button type="button" className="btn" disabled={busy} onClick={() => run("customize")}>AI Customize G-code again</button>
        <button type="button" className="btn" disabled={busy} onClick={() => run("gcode")}>Attach another G-code</button>
      </div>
    </div>
  );
}

export function PrintLoopStrip({ record, onLoop, current }) {
  const pack = aiStudioPackage(record);
  const ret = latestStudioReturn(record);
  const needsReturn = Boolean(pack) && (!ret || (ret.source_package_id && pack.id && ret.source_package_id !== pack.id));
  const steps = [
    { id: "ai_studio", label: "AI Studio", done: Boolean(pack) },
    { id: "studio_return", label: "Return", done: Boolean(ret) && !needsReturn },
    { id: "orca", label: "Orca", done: Boolean(orcaHandoff(record)?.files?.length) },
    { id: "orca_bridge", label: "Orca check", title: "Orca Bridge check. CHECKED means package checks passed, not physical printer validation. purge_reduction_applied stays false.", done: Boolean(latestOrcaBridge(record)?.status === "CHECKED" && latestOrcaBridge(record)?.purge_reduction_applied === false) },
    { id: "customize", label: "Customize", done: Boolean(latestCustomize(record)?.sd_ready) },
    { id: "gcode", label: "G-code", done: gcodeDrops(record).length > 0 || Boolean(latestCustomize(record)?.sd_ready) },
  ];
  return (
    <div className="bw-print-loop" data-testid="forgelink-print-loop">
      {steps.map((step) => (
        <button type="button" key={step.id} className={[step.done ? "is-done" : "", step.id === current ? "is-current" : ""].filter(Boolean).join(" ")} onClick={() => onLoop?.(step.id)} title={step.title || step.label} data-testid={`forgelink-loop-step-${step.id}`}>
          <small>{step.done ? "Done" : "Open"}</small>
          <b>{step.label}</b>
        </button>
      ))}
    </div>
  );
}

export function ProductThumb({ businessKey, record, alt }) {
  const preview = resolveRecordPreview(businessKey, record);
  if (!preview.path && !preview.externalUrl) {
    return <span className="bw-thumb bw-thumb-empty" aria-hidden="true" />;
  }
  return (
    <span className="bw-thumb">
      <AuthImage
        path={preview.path}
        src={preview.externalUrl}
        alt={alt || record?.title || ""}
        placeholderClassName="bw-thumb-ph"
        loadingLabel=""
        errorLabel=""
      />
    </span>
  );
}

export function ProductCard({ businessKey, record, selected, onSelect }) {
  const next = nextPathwayAction(record);
  return (
    <button
      type="button"
      className={`bw-product-card${selected ? " is-selected" : ""}`}
      onClick={() => onSelect?.(record)}
      data-testid="forgelink-product-card"
      data-edit-name={`${displayText(record?.title) || "Product"} card`}
    >
      <ProductThumb businessKey={businessKey} record={record} alt={record?.title} />
      <span>
        <b>{displayText(record?.title)}</b>
        <small>{pipelineStageLabel(pipelineStage(record))} · {next.label}</small>
      </span>
    </button>
  );
}

export function PipelineTrack({ stage }) {
  const index = Math.max(0, PIPELINE_STAGES.findIndex((row) => row.id === stage));
  return (
    <ol className="bw-pipeline-track" aria-label={`Stage: ${pipelineStageLabel(stage)}`}>
      {PIPELINE_STAGES.map((row, itemIndex) => (
        <li key={row.id} className={itemIndex < index ? "is-done" : itemIndex === index ? "is-current" : ""}>
          <span>{row.label}</span>
        </li>
      ))}
    </ol>
  );
}

export function RecordPreview({ businessKey, record, variant = "card" }) {
  const preview = resolveRecordPreview(businessKey, record);
  const hasImage = Boolean(preview.path || preview.externalUrl);
  return (
    <span className={variant === "detail" ? "bw-work-preview" : "bw-object-preview"}>
      {hasImage ? (
        <AuthImage
          path={preview.path}
          src={preview.externalUrl}
          alt={record?.title || "Product image"}
          placeholderClassName="bw-object-empty"
          loadingLabel="Loading image…"
          errorLabel="Image unavailable"
        />
      ) : (
        <span className="bw-object-empty">No image yet</span>
      )}
    </span>
  );
}

export function ProductPipelineRow({ businessKey, record, selected, onOpen }) {
  const stage = pipelineStage(record);
  return (
    <button
      type="button"
      className={`bw-object-card${selected ? " is-selected" : ""}`}
      onClick={() => onOpen?.(record)}
      data-testid="forgelink-product-card"
      data-edit-name={`${displayText(record?.title) || "Product"} card`}
    >
      <RecordPreview businessKey={businessKey} record={record} />
      <span className="bw-object-copy">
        <b>{displayText(record?.title)}</b>
        <span className="bw-object-facts">
          <span className={`bw-stage-badge is-${stage}`}>{pipelineStageLabel(stage)}</span>
          <span className={`bw-status-text is-${workStatusTone(record)}`}>{workStatusLabel(record)}</span>
          <time className="bw-updated" dateTime={record?.updated_at || record?.created_at || ""}>{relativeUpdated(record)}</time>
        </span>
      </span>
    </button>
  );
}

export function HubWorkPane({
  businessKey,
  work,
  record,
  onClose,
  onDesk,
  onPreview,
  onLoop,
  onCreated,
  onAttached,
  onPrototypeSaved,
  onLoopSaved,
}) {
  useEffect(() => {
    if (!work) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [work, onClose]);
  if (!work) return null;
  const fresh = record || work.record || null;
  const creating = work.type === "new-concept";
  const stage = fresh ? pipelineStage(fresh) : "idea";
  const next = fresh ? nextPathwayAction(fresh) : null;
  const runNext = () => {
    if (!fresh || !next) return;
    if (next.id === "design") onDesk?.(fresh);
    else if (next.id === "render") onPreview?.(fresh);
    else onLoop?.(next.id, fresh);
  };
  const showWorkButton = Boolean(fresh && next && (work.type === "product" || work.type === "preview"));
  return (
    <div
      className="bw-work-overlay"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <aside className="bw-work" role="dialog" aria-modal="true" aria-label={creating ? "Create product" : displayText(fresh?.title || "Product")} data-testid="forgelink-work-pane" data-edit-name={creating ? "Create product" : "ForgeLinc product"}>
        <header>
          <div>
            <small>{creating ? "New product" : pipelineStageLabel(stage)}</small>
            <b>{creating ? "Create product" : displayText(fresh?.title || "Product")}</b>
          </div>
          <button type="button" onClick={onClose} aria-label="Close">Close</button>
        </header>
        <div className="bw-work-body">
          {creating ? <ConceptForm businessKey={businessKey} onCreated={onCreated} /> : null}
          {!creating && fresh ? (
            <>
              <RecordPreview businessKey={businessKey} record={fresh} variant="detail" />
              <div className="bw-work-facts">
                <span className={`bw-stage-badge is-${stage}`}>{pipelineStageLabel(stage)}</span>
                <span className={`bw-status-text is-${workStatusTone(fresh)}`}>{workStatusLabel(fresh)}</span>
                <time className="bw-updated">{relativeUpdated(fresh)}</time>
              </div>
              <p className="bw-work-note">{displayText(fresh.description || "No notes yet.")}</p>
              {next ? <p className="bw-work-next">Next: {next.label}</p> : null}
              {showWorkButton ? (
                <div className="bw-work-primary">
                  <button
                    type="button"
                    className="btn primary"
                    onClick={runNext}
                    title={next.hint}
                    data-testid={`forgelink-next-${next.id}`}
                  >
                    {next.id === "design" ? "Open Design Desk" : "Work on it"}
                  </button>
                </div>
              ) : null}
              {work.type === "attach-design" ? <DesignAttachForm businessKey={businessKey} record={fresh} onAttached={onAttached} /> : null}
              {work.type === "prototype-note" || work.type === "prototype" ? (
                <PrototypeNoteForm businessKey={businessKey} record={fresh} onSaved={onPrototypeSaved} />
              ) : null}
              {work.type === "ai_studio" ? <AiStudioHandoffForm businessKey={businessKey} record={fresh} onSaved={onLoopSaved} /> : null}
              {work.type === "studio_return" ? <StudioReturnForm businessKey={businessKey} record={fresh} onSaved={onLoopSaved} /> : null}
              {work.type === "orca" ? <OrcaHandoffForm businessKey={businessKey} record={fresh} onSaved={onLoopSaved} /> : null}
              {work.type === "orca_bridge" ? <OrcaBridgeForm businessKey={businessKey} record={fresh} onSaved={onLoopSaved} /> : null}
              {work.type === "customize" ? <CustomizeGcodeForm businessKey={businessKey} record={fresh} onSaved={onLoopSaved} /> : null}
              {work.type === "gcode" ? <GcodeDropForm businessKey={businessKey} record={fresh} onSaved={onLoopSaved} /> : null}
              {work.type === "next_cook" ? (
                <NextCookForm businessKey={businessKey} record={fresh} onDesk={onDesk} onLoop={onLoop} onSaved={onLoopSaved} />
              ) : null}
            </>
          ) : null}
        </div>
      </aside>
    </div>
  );
}

export function CurrentProductPanel({
  businessKey,
  record,
  onDesk,
  onAttach,
  onPreview,
  onPrototype,
  onPatch,
  onHandoff,
  onLoop,
  handoffBusy,
}) {
  const preview = resolveRecordPreview(businessKey, record);
  const next = nextPathwayAction(record);
  const showImage = Boolean(preview.path || preview.externalUrl);
  const runNext = () => {
    if (next.id === "design") return (onDesk || onAttach)?.(record);
    if (next.id === "render") return onPreview?.(record);
    if (next.id === "prototype") return onPrototype?.(record);
    return onLoop?.(next.id, record);
  };
  return (
    <article className="bw-card bw-current-product" data-testid="forgelink-current-product" data-edit-name="Current product">
      <header>
        <div>
          <Sparkles size={16} />
          <span>
            <b>Current product</b>
            <small>Selected in this window — Design Desk through SD-ready G-code on the {FORGELINC_NAME} hub</small>
          </span>
        </div>
        {record ? <em className={`pill ${record.status === "blocked" ? "amber" : "cyan"}`}>{String(record.status || "").replace(/_/g, " ")}</em> : null}
      </header>
      <div className="bw-current-body">
        <div className="bw-current-media" data-testid="forgelink-current-media">
          {showImage ? (
            <AuthImage
              path={preview.path}
              src={preview.externalUrl}
              alt={record?.title || "Current product"}
              placeholderClassName="bw-current-empty"
            />
          ) : (
            <div className="bw-current-empty">{record ? "No render yet" : "Create a product to start"}</div>
          )}
        </div>
        <div className="bw-current-meta">
          <small>Active development record</small>
          <b>{record ? displayText(record.title) : "No product selected"}</b>
          <p>{record ? displayText(record.description || next.hint) : "Idea → Design Desk → Render → Google AI Studio → Orca → Orca Bridge check → AI Customize → SD-ready"}</p>
          {record ? <p className="bw-current-next">Next: {next.label}</p> : null}
          {record ? <PrintLoopStrip record={record} onLoop={(action) => onLoop?.(action, record)} /> : null}
        </div>
        <div className="bw-current-side">
          {record ? (
            <>
              <div className="bw-current-actions">
                <Link className="btn primary" to={designDeskPath(record.id)} onClick={() => onDesk?.(record)}>Open design desk</Link>
                <button type="button" className="btn" onClick={runNext} data-testid={`forgelink-current-next-${next.id}`}>
                  {next.label} <ArrowRight size={13} />
                </button>
              </div>
              <ArtifactSummary record={record} />
              <div className="bw-current-actions bw-current-status-actions">
                <button type="button" className="btn small" onClick={() => onPatch?.(record, { status: "in_progress" })}>In progress</button>
                <button type="button" className="btn small" onClick={() => onPatch?.(record, { status: "qa" })}>QA</button>
                <button type="button" className="btn small primary" onClick={() => onHandoff?.(record)} disabled={handoffBusy}>
                  {handoffBusy ? "Handing off…" : "Ready for Store"}
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </article>
  );
}

export function DesignPreview({ businessKey, record }) {
  const artifact = designArtifact(record);
  const preview = resolveRecordPreview(businessKey, record);
  const showImage = Boolean(preview.path || preview.externalUrl);

  if (!artifact && !showImage) {
    return <p className="hint">No render yet. Open Design Desk to generate or upload a design.</p>;
  }

  return (
    <div className="bw-preview" data-testid="forgelink-preview">
      {showImage ? (
        <AuthImage
          path={preview.path}
          src={preview.externalUrl}
          alt={artifact?.filename || record.title}
          placeholderClassName="bw-current-empty"
        />
      ) : artifact ? (
        <div className="bw-preview-file">
          <ImageIcon size={22} />
          <div>
            <b>{artifact.filename}</b>
            <small>{artifact.kind} · {artifact.source}{artifact.byte_size ? ` · ${artifact.byte_size} bytes` : ""}</small>
            <p>{artifact.kind === "image" ? "Image preview is not available." : "This file is stored. Upload a render image for a visual preview; a 3D viewer is not in this slice."}</p>
          </div>
        </div>
      ) : null}
      {artifact?.url ? <p className="hint">Source: <a href={artifact.url} target="_blank" rel="noreferrer">{artifact.url}</a></p> : null}
      {artifact?.notes ? <p>{artifact.notes}</p> : null}
    </div>
  );
}

export function NextActionBar({ record, onCreate, onAttach, onPreview, onPrototype, onDesk, onLoop }) {
  const next = nextPathwayAction(record);
  const run = () => {
    if (next.id === "create") return onCreate?.();
    if (next.id === "design") return (onDesk || onAttach)?.(record);
    if (next.id === "render") return onPreview?.(record);
    if (next.id === "prototype") return onPrototype?.(record);
    return onLoop?.(next.id, record);
  };
  return (
    <div className="bw-next-action" data-testid="forgelink-next-action">
      <span>
        <small>Next action</small>
        <b>{record ? `${record.title} · ${next.label}` : next.label}</b>
        <em>{next.hint}</em>
      </span>
      <span className="bw-next-buttons">
        {record ? (
          <Link className="btn" to={designDeskPath(record.id)} data-testid="forgelink-open-desk">
            Open design desk
          </Link>
        ) : null}
        <button type="button" className="btn primary" onClick={run} data-testid={`forgelink-next-${next.id}`}>
          {next.label} <ArrowRight size={13} />
        </button>
      </span>
    </div>
  );
}

export function RecordPathwayActions({ record, onAttach, onPreview, onPrototype, onDesk, onLoop }) {
  return <NextActionBar record={record} onAttach={onAttach} onPreview={onPreview} onPrototype={onPrototype} onDesk={onDesk} onLoop={onLoop} />;
}

export function FlowStagePanel({ businessKey, step, records, onOpenRecord, onAttach, onPreview, onPrototype, onCreate, onDesk, onLoop, conceptForm }) {
  const isIdea = step.key === "concept";
  const isDesign = step.key === "design";
  const isRender = step.key === "render";
  const isLoop = step.key === "ai_studio" || step.key === "orca" || step.key === "orca_bridge" || step.key === "customize" || step.key === "gcode";
  const wired = isIdea || isDesign || isRender || isLoop;

  return (
    <div className="bw-detail" data-testid={`forgelink-stage-${step.key}`}>
      <h3>{step.label}</h3>
      <p>{step.note}</p>
      {isIdea ? conceptForm : null}
      {!wired ? <p className="hint">Later stage. Use Next action on the active product.</p> : null}
      {!records.length && !isIdea ? (
        <NextActionBar record={null} onCreate={onCreate} />
      ) : null}
      <div className="bw-overlay-list bw-stage-records">
        {records.length ? records.map((row) => {
          const next = nextPathwayAction(row);
          return (
            <div key={row.id} className="bw-stage-record">
              <button type="button" className="bw-product-row" onClick={() => onOpenRecord(row)}>
                <ProductThumb businessKey={businessKey} record={row} alt={row.title} />
                <span><b>{row.title}</b><small>Next: {next.label}</small></span>
                <em>{pathwayLabel(row)}</em>
              </button>
              <div className="bw-stage-record-actions">
                <Link className="btn small" to={designDeskPath(row.id)}>Open design desk</Link>
                <button type="button" className="btn small primary" onClick={() => {
                  if (next.id === "design") (onDesk || onAttach)(row);
                  else if (next.id === "render") onPreview(row);
                  else if (next.id === "prototype") onPrototype(row);
                  else onLoop?.(next.id, row);
                }}>{next.label}</button>
              </div>
            </div>
          );
        }) : isIdea ? <p>No products yet. Use Create product above.</p> : null}
      </div>
    </div>
  );
}

export function ArtifactSummary({ record }) {
  const artifact = designArtifact(record);
  if (!artifact) return null;
  return (
    <div className="bw-artifact-chip">
      <Sparkles size={14} />
      <span>
        <b>{artifact.filename}</b>
        <small>{artifact.kind} · {artifact.source}{artifact.previewable ? " · render ready" : ""}</small>
      </span>
      {artifact.source === "link" ? <Link2 size={14} /> : <FileUp size={14} />}
    </div>
  );
}
